/* eslint-disable no-undef */
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export interface NavigationOptions {
  replace?: boolean;
  state?: any;
  resetScroll?: boolean;
  forceRefresh?: boolean;
}

interface UseAppNavigationReturn {
  navigateTo: (to: string, options?: NavigationOptions) => void;
  currentPath: string;
  previousPath: string | null;
  isNavigating: boolean;
}

/**
 * Custom navigation hook that wraps React Router's useNavigate
 * with additional functionality to ensure proper route cleanup
 * and state management during navigation transitions.
 */
export const useAppNavigation = (): UseAppNavigationReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const previousPathRef = useRef<string | null>(null);
  const isNavigatingRef = useRef(false);
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Update previous path after navigation completes
    if (previousPathRef.current !== location.pathname) {
      previousPathRef.current = location.pathname;
      isNavigatingRef.current = false;
    }
  }, [location.pathname]);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  const navigateTo = useCallback((to: string, options?: NavigationOptions) => {
    // Prevent multiple rapid navigations
    if (isNavigatingRef.current) {
      return;
    }

    // Mark as navigating
    isNavigatingRef.current = true;

    // Clear any existing navigation timeout
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }

    // Handle scroll reset
    if (options?.resetScroll !== false) {
      window.scrollTo(0, 0);
    }

    // Handle force refresh
    if (options?.forceRefresh) {
      // Use a small delay to ensure state cleanup
      navigationTimeoutRef.current = setTimeout(() => {
        window.location.href = to;
      }, 50);
      return;
    }

    // Perform navigation with a small delay to ensure cleanup
    navigationTimeoutRef.current = setTimeout(() => {
      navigate(to, {
        replace: options?.replace,
        state: options?.state,
      });
      
      // Reset navigation flag after a delay
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 100);
    }, 0);
  }, [navigate]);

  return {
    navigateTo,
    currentPath: location.pathname,
    previousPath: previousPathRef.current,
    isNavigating: isNavigatingRef.current,
  };
};